import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Users } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { wardScope } from "@/lib/patient";
import { Cell, DataTable, MetricCard, PageHeader, Panel, PanelHeader, Row } from "./ui";

const inputClass =
  "h-11 w-full min-w-0 rounded-xl border border-border bg-white px-3.5 text-[14px] text-ink-800 focus:border-sage-600 focus:outline-none focus:ring-2 focus:ring-sage-600/15";

const WARD_LABELS = { all: "Todas as alas", MASCULINA: "Ala Masculina", FEMININA: "Ala Feminina" };

const Field = ({ label, children }) => (
  <label className="flex min-w-0 flex-col gap-1 text-[13px] text-ink-600">
    {label}
    {children}
  </label>
);

export default function PaymentMetrics() {
  const { api, user } = useAuth();
  const scope = wardScope(user);
  const [filters, setFilters] = useState({ start: "", end: "", status: "all", ward: scope.value });
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    setData(null);
    try {
      const { data: payload } = await api.get("/portal/metrics/payments", {
        params: {
          start: filters.start || undefined,
          end: filters.end || undefined,
          status: filters.status,
          ward: filters.ward || "all",
        },
      });
      setData(payload);
    } catch {
      setData(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.start, filters.end, filters.status, filters.ward]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div data-testid="payment-metrics-page" className="space-y-6">
      <Link
        to="/portal/metricas"
        data-testid="payment-metrics-back"
        className="inline-flex items-center gap-2 text-[14px] font-semibold text-ink-700 hover:text-ink-900"
      >
        <ArrowLeft className="h-4 w-4" /> Voltar para métricas
      </Link>
      <PageHeader
        kicker="Meios de pagamento"
        title="Métricas de meios de pagamento"
        subtitle="Dados cadastrais de convênio e pagamento — não representam recebimentos financeiros."
      />

      <Panel testId="payment-metrics-filters" className="space-y-4">
        <PanelHeader
          label="Filtros"
          title="Período de tratamento, ala e situação atual"
          hint={
            data
              ? `Período: ${data.period.start || "início dos registros"} a ${data.period.end || "hoje"} (limites inclusivos) · ${
                  scope.locked ? scope.label : WARD_LABELS[filters.ward] || "Todas as alas"
                }. A situação é a atual do paciente.`
              : "O período considera pacientes que estiveram em tratamento em algum momento do intervalo (limites inclusivos)."
          }
          actions={
            <div className="flex w-full min-w-0 flex-wrap items-end gap-2">
              <Field label="Data inicial">
                <input
                  type="date"
                  data-testid="payment-metrics-start"
                  value={filters.start}
                  onChange={(e) => setFilters({ ...filters, start: e.target.value })}
                  className={inputClass}
                />
              </Field>
              <Field label="Data final">
                <input
                  type="date"
                  data-testid="payment-metrics-end"
                  value={filters.end}
                  onChange={(e) => setFilters({ ...filters, end: e.target.value })}
                  className={inputClass}
                />
              </Field>
              <Field label="Situação atual">
                <select
                  data-testid="payment-metrics-status"
                  value={filters.status}
                  onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                  className={inputClass}
                >
                  <option value="all">Todos</option>
                  <option value="active">Ativos</option>
                  <option value="inactive">Inativos</option>
                </select>
              </Field>
              {scope.locked ? (
                <span
                  data-testid="payment-metrics-ward-scope"
                  className="inline-flex h-11 items-center rounded-xl bg-sand px-3.5 text-[14px] font-semibold text-ink-700"
                >
                  {scope.label}
                </span>
              ) : (
                <Field label="Ala">
                  <select
                    data-testid="payment-metrics-ward"
                    value={filters.ward}
                    onChange={(e) => setFilters({ ...filters, ward: e.target.value })}
                    className={inputClass}
                  >
                    <option value="all">Todas as alas</option>
                    <option value="MASCULINA">Masculina</option>
                    <option value="FEMININA">Feminina</option>
                  </select>
                </Field>
              )}
            </div>
          }
        />
      </Panel>

      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        <MetricCard
          testId="payment-metric-total"
          label="Pacientes únicos no filtro"
          value={data?.total_patients ?? 0}
          icon={Users}
        />
        <MetricCard
          testId="payment-metric-incomplete"
          label="Dados incompletos"
          value={data?.incomplete?.length ?? 0}
          hint="Sem datas suficientes para definir presença no período"
          alert={(data?.incomplete?.length ?? 0) > 0}
        />
        <MetricCard
          testId="payment-metric-plans"
          label="Planos distintos"
          value={data?.by_plan?.length ?? 0}
          hint="Inclui Não aplicável e Não informado"
        />
      </section>

      <div className="grid gap-5 xl:grid-cols-2">
        {data === null ? (
          <p data-testid="payment-metrics-loading" className="text-[14px] text-ink-500">
            Carregando métricas…
          </p>
        ) : data === false ? (
          <p className="text-[14px] text-ink-500">Não foi possível carregar as métricas agora.</p>
        ) : data.total_patients === 0 ? (
          <p data-testid="payment-metrics-empty" className="text-[14px] text-ink-500">
            Nenhum paciente encontrado para os filtros selecionados.
          </p>
        ) : (
          <>
        <Panel testId="payment-metrics-methods" className="space-y-4">
          <PanelHeader label="Meios de pagamento" title="Pacientes por meio" hint={data?.classification_note} />
          <DataTable
            testId="payment-metrics-methods-table"
            columns={[
              { key: "method", label: "Meio de pagamento" },
              { key: "patients", label: "Pacientes" },
            ]}
          >
            {(data?.by_method || []).map((row) => (
              <Row key={row.value} testId={`payment-metrics-method-${row.value}`}>
                <Cell>{row.label}</Cell>
                <Cell>{row.patients}</Cell>
              </Row>
            ))}
          </DataTable>
        </Panel>

        <Panel testId="payment-metrics-plans" className="space-y-4">
          <PanelHeader
            label="Planos de saúde"
            title="Pacientes por plano"
            hint="“Não aplicável” quando não há plano por natureza; “Não informado” quando o cadastro está incompleto."
          />
          <DataTable
            testId="payment-metrics-plans-table"
            columns={[
              { key: "plan", label: "Plano" },
              { key: "patients", label: "Pacientes" },
            ]}
          >
            {(data?.by_plan || []).map((row) => (
              <Row key={row.plan} testId={`payment-metrics-plan-${row.plan}`}>
                <Cell>{row.plan}</Cell>
                <Cell>{row.patients}</Cell>
              </Row>
            ))}
          </DataTable>
        </Panel>
          </>
        )}
      </div>
    </div>
  );
}
